import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { siteConfig, categories } from '@/mocks/categories';
import { getAllArticleSlugs, getArticleBySlug } from '@/mocks/articles';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const allSlugs = getAllArticleSlugs();
  const mainGuideSlug = allSlugs[0] || 'superbuy-spreadsheet-guide';

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || mobileOpen
            ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2 text-foreground-950 hover:text-primary-600 transition-colors duration-200"
            onClick={() => setMobileOpen(false)}
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-grid-line text-lg"></i>
            </div>
            <span className="font-heading text-xl font-semibold tracking-tight whitespace-nowrap">
              {siteConfig.siteName}
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <a
              href="#categories"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-full hover:bg-background-100 transition-all duration-200 whitespace-nowrap"
            >
              Categories
            </a>
            <a
              href="#about"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-full hover:bg-background-100 transition-all duration-200 whitespace-nowrap"
            >
              About
            </a>
            <Link
              to={`/article/${mainGuideSlug}`}
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-full hover:bg-background-100 transition-all duration-200 whitespace-nowrap"
            >
              Guide
            </Link>
            <a
              href={siteConfig.mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="ml-2 px-5 py-2 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <div className="flex items-center gap-2 md:hidden">
            <a
              href={siteConfig.mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-1.5 text-xs font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors duration-200 cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-300 md:hidden ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMobileOpen(false)}
      ></div>

      {/* Mobile Menu Panel */}
      <div
        className={`fixed top-16 right-0 bottom-0 w-[300px] max-w-[85vw] bg-background-50 z-40 transition-transform duration-300 ease-in-out overflow-y-auto md:hidden ${
          mobileOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="p-5">
          {/* Nav Links */}
          <div className="mb-6">
            <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Navigation</h4>
            <div className="flex flex-col gap-1">
              <a
                href="#categories"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-all duration-200"
              >
                <div className="w-7 h-7 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-600">
                  <i className="ri-grid-line text-sm"></i>
                </div>
                Categories
              </a>
              <a
                href="#about"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-all duration-200"
              >
                <div className="w-7 h-7 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-600">
                  <i className="ri-information-line text-sm"></i>
                </div>
                About
              </a>
              <Link
                to={`/article/${mainGuideSlug}`}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-all duration-200"
              >
                <div className="w-7 h-7 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-600">
                  <i className="ri-book-open-line text-sm"></i>
                </div>
                Guide
              </Link>
            </div>
          </div>

          {/* All Categories */}
          <div className="mb-6">
            <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Product Categories</h4>
            <div className="flex flex-col gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.seq}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-primary-50/50 rounded-lg transition-all duration-200 cursor-pointer"
                >
                  <div className="w-7 h-7 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-600">
                    <i className={`${cat.icon} text-sm`}></i>
                  </div>
                  {cat.name}
                </a>
              ))}
            </div>
          </div>

          {/* SEO Articles */}
          <div className="mb-6">
            <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">SEO Guides</h4>
            <div className="flex flex-col gap-1">
              {allSlugs.map((s) => {
                const a = getArticleBySlug(s);
                return (
                  <Link
                    key={s}
                    to={`/article/${s}`}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-primary-50/50 rounded-lg transition-all duration-200"
                  >
                    <div className="w-7 h-7 flex items-center justify-center rounded-md bg-accent-100 text-accent-600">
                      <i className="ri-article-line text-sm"></i>
                    </div>
                    <span className="whitespace-nowrap truncate">{a?.badge || s}</span>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Bottom CTA */}
          <a
            href={siteConfig.mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            onClick={() => setMobileOpen(false)}
            className="flex items-center justify-center gap-2 w-full px-5 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            Buy It Now
            <i className="ri-arrow-right-up-line"></i>
          </a>
        </div>
      </div>
    </>
  );
}